/* eslint-disable @reatom/unit-naming-rule */
import { atom, withAssign } from '@reatom/framework'

import { AsyncStateN } from '@blade/std-lib'
import { networkDataNativePriceAtom } from '@root/entities/network/atoms'

import { toTradingAnalytics } from '../model'

import { reatomWalletProfile } from './reatom-wallet-profile'

import type { Address } from '@root/entities/address/Address'
import type { Network } from '@root/entities/network'

/**
 * One wallet, read once and shown two ways.
 *
 * The dashboard wants a `UserTradingAnalytics` and the positions list wants
 * the positions, and both are in the one answer — so the request is the
 * source and the analytics are derived from it rather than fetched again.
 *
 * The derived atom carries `refresh` because that is all the dashboard's glue
 * asks of its source besides reading it; its retry button lands back on this
 * request.
 */
export const reatomWalletProfileModel = ({
  walletAddress,
  network,
}: {
  walletAddress: Address
  network: Network.Network
}) => {
  const profileAtom = reatomWalletProfile({ walletAddress, network })

  const analyticsAtom = atom((ctx) => {
    // In dollars or in the native asset, whichever the reader picked; the
    // figures are computed in dollars and divided by this.
    const nativePrice = Number(ctx.spy(networkDataNativePriceAtom).value ?? 0)

    return AsyncStateN.map(ctx.spy(profileAtom), (profile) =>
      toTradingAnalytics(profile, network, nativePrice),
    )
  }, 'walletAnalyticsAtom').pipe(
    withAssign(() => ({ refresh: profileAtom.refresh })),
  )

  return { profileAtom, analyticsAtom }
}

export type WalletProfileModel = ReturnType<typeof reatomWalletProfileModel>
